import React from "react";

import Timeline from "./CovidWorldResponseTimeline";
import Policies from "./CovidWorldResponsePolicies";
import CovidWorldResponsePlots from "./CovidWorldResponsePlots";
import TestPolicy from "./CovidWorldResponseTestPolicy";
import TestPerCapita from "./CovidWorldResponseTestPerCapita";
import deathsPlot from "./assets/deaths.svg";

function CovidWorldResponse() {
  return (
    <>
      <p>
        <em>
          "Je ne laisserai personne dire qu'il y a eu du retard sur la prise de
          décision s'agissant du confinement"
        </em>
        . C’est ainsi que s’exprimait Edouard Philippe, samedi 28 mars, dans une
        allocution télévisée. Cependant, une analyse comparative des réactions
        de dizaines de pays face à la pandémie montre que le gouvernement
        français fait partie de ceux ayant agi au stade le plus avancé de
        l'épidémie, et demeure en retard en terme de politique de tests.
      </p>
      <p>
        Des chercheurs de l’Université d’Oxford ont mis au point{" "}
        <a
          href="https://covidtracker.bsg.ox.ac.uk/"
          target="_blank"
          rel="noreferrer noopener"
        >
          une base de données
        </a>{" "}
        recensant jour par jour, pour une centaine de pays, les différentes
        mesures mises en place dans la lutte contre le Covid-19, parmi
        lesquelles&nbsp;:
      </p>
      <ul>
        <li>les fermetures nationales des écoles ;</li>
        <li>les fermetures nationales des lieux de travail ;</li>
        <li>les premières annulations d’événements publics ;</li>
        <li>les mises à l’arrêt des transports en commun ;</li>
        <li>les restrictions des déplacements à l’intérieur des pays ;</li>
        <li>les interdictions des voyages à partir des pays à risque ;</li>
        <li>
          les tests systématiques pour toutes les personnes symptomatiques ;
        </li>
        <li>
          le traçage occasionnel des contacts (pas nécessairement au travers
          d'une application).
        </li>
      </ul>
      <p>
        Ces données permettent de reconstituer la chronologie des mesures mises
        en place par les gouvernements de différents pays face à la crise&nbsp;:
      </p>

      <Timeline />

      <p>
        Comment comparer la réactivité de différents gouvernements ? Se
        focaliser sur la date de mise en vigueur des mesures d’endiguement
        n’aurait qu’un sens très limité, car la donnée-clé est le stade de
        l'épidémie au moment où ces mesures ont été mises en place. Les pays
        atteints le plus tard par l’épidémie n’avaient en effet pas de raison
        d’ordonner un confinement généralisé trop en avance. En revanche, ceux
        où la propagation avait déjà démarré devaient agir très vite pour
        contenir l'épidémie, car dans la phase exponentielle le nombre
        d'infections double en quelques jours seulement. On peut donc calculer,
        selon une méthode suggérée par{" "}
        <a
          href="https://github.com/juliengossa/covid19"
          target="_blank"
          rel="noreferrer noopener"
        >
          Julien Gossa
        </a>
        , dans chaque pays et pour chaque mesure d’endiguement, le nombre de
        jours qui se sont écoulés entre sa mise en place et le franchissement
        d’un certain seuil de l’épidémie. Par exemple, le nombre de jours
        écoulés entre la fermeture des écoles et le dixième décès. Ces délais
        d’action constituent alors une mesure du niveau d’anticipation (ou au
        contraire d’impréparation) des gouvernements.
      </p>

      <h2>L’incroyable lenteur de la France dans les mesures de confinement</h2>
      <p>
        Nous avons réalisé cette analyse pour 42 pays comprenant les membres de
        l’OCDE ainsi que l’Argentine, le Brésil, la Chine, l’Inde, l’Indonésie,
        Taïwan, la Russie, l’Arabie Saoudite et l’Afrique du Sud. Les résultats
        sont disponibles ci-dessous, mesure par mesure, classés soit par date
        d'application, soit par délai de mise en place.
      </p>

      <CovidWorldResponsePlots />

      <p>
        Si l'on s'en tient au classement chronologique, la France a agi à peu
        près dans la médiane des autres pays de l'OCDE. Cependant, le virus
        l'ayant gagnée plus tôt que la plupart des autres pays, elle a agi alors
        que l'épidémie était déjà à un stade avancé. Elle figure ainsi dans le
        fond du classement en terme de délai d'adoption par rapport au
        franchissement des 10 décès.
      </p>

      <Policies />

      <p>
        Quelle que soit la mesure considérée, et indépendamment du seuil retenu,
        la France fait partie des pays ayant réagi au stade le plus avancé de
        l'épidémie.
      </p>

      <p>
        Parmi les pays à la traîne, on trouve également les États-Unis, dont la
        réaction s'est faite tardivement au niveau fédéral. Retard également
        pour le Royaume-Uni et les Pays-Bas, qui ont en commun d’
        <a
          href="https://www.theatlantic.com/health/archive/2020/03/coronavirus-pandemic-herd-immunity-uk-boris-johnson/608065/"
          target="_blank"
          rel="noreferrer noopener"
        >
          avoir initialement fait le choix
        </a>{" "}
        de{" "}
        <a
          href="https://www.vice.com/en_us/article/939mez/the-netherlands-is-letting-a-lot-of-people-get-sick-to-try-to-beat-coronavirus"
          target="_blank"
          rel="noreferrer noopener"
        >
          laisser circuler le virus afin d’atteindre le seuil
        </a>{" "}
        d’
        <a
          href="https://lemediatv.fr/emissions/2020/data-face-au-coronavirus-le-pari-mortel-de-limmunite-collective-st8-Yg2BSDWB7dcMPMW6sA"
          target="_blank"
          rel="noreferrer noopener"
        >
          immunité collective
        </a>
        . La proximité de ces pays avec la France dans le classement n’est pas
        un hasard : tous les éléments suggèrent qu’elle avait choisi{" "}
        <a
          href="https://www.lefigaro.fr/sciences/la-france-mise-sur-l-immunite-de-groupe-pour-arreter-le-coronavirus-20200313"
          target="_blank"
          rel="noreferrer noopener"
        >
          la même direction
        </a>
        . C’est ainsi que Sibeth Ndiaye avait par exemple déclaré, le 4 mars,
        lors d’une interview pour{" "}
        <a
          href="https://www.franceinter.fr/emissions/l-invite-de-8h20-le-grand-entretien/l-invite-de-8h20-le-grand-entretien-04-mars-2020"
          target="_blank"
          rel="noreferrer noopener"
        >
          France Inter
        </a>{" "}
        : “<em>Si le virus circule, on laissera les gens vivre</em>”. La France
        semble ainsi faire partie des quelques pays qui ont délibérément tardé à
        agir.
      </p>

      <h2>Un retard aux conséquences dramatiques</h2>

      <p>
        Afin d'estimer l'impact du retard de la France dans les mesures de
        confinement, nous avons simulé l'évolution du nombre de décès dans
        l'hypothèse où les mesures auraient été prises plus tôt. Pour cela, nous
        avons adapté{" "}
        <a
          href="https://www.imperial.ac.uk/mrc-global-infectious-disease-analysis/covid-19/report-13-europe-npi-impact/"
          target="_blank"
          rel="noreferrer noopener"
        >
          un modèle réalisé par l'Imperial College de Londres
        </a>
        , et dont{" "}
        <a
          href="https://github.com/ImperialCollegeLondon/covid19model"
          target="_blank"
          rel="noreferrer noopener"
        >
          le code source est librement accessible
        </a>
        .
      </p>

      <p>
        Le résultat de cette simulation est visible sur la figure ci-dessous. On
        y observe le nombre de décès relevés dans les hôpitaux, et l'estimation
        du nombre de décès si toutes les mesures de confinement avaient été
        prises simultanément le 11 mars (hypothèse 1) et le 13 mars (hypothèse
        2), plutôt que d'imposer ces mesures progressivement jusqu'au 17 mars.
      </p>

      <figure>
        <img
          src={deathsPlot}
          alt="Le nombre cumulés de décès attribués au Covid-19
        en milieu hospitalier au 27 avril est d'environ 14.000.
        Si les mesure de confinement avaient été appliquées dès le 13 mars,
        on estime que ce nombre serait compris entre 6.000 et 11.000.
        Si elles avaient été imposées dès le 11 mars,
        on estime que ce nombre se situerait entre 3.000 et 7.000."
        />
        <figcaption>
          <strong>
            Estimation du nombre de décès provoqués par le Covid-19 pour
            différentes dates d'application du confinement.
          </strong>
          .<br />
          Les points noirs représentent le nombre de décès attribués au Covid-19
          et survenus dans les hôpitaux. Les marges d'erreur traduisent la
          nature aléatoire du processus, l'incertitude sur les différents
          paramètres épidémiologiques ainsi que la difficulté à estimer l'impact
          des mesures de confinement.
        </figcaption>
      </figure>

      <p>
        On estime ainsi qu'entre 3.000 et 8.000 décès auraient pu être évités
        parmi ceux survenus en milieu hospitalier à ce jour si les mesures
        avaient été appliquées dès vendredi 13 mars. Si le confinement avait été
        appliqué dès le mercredi 11 mars, on estime que le nombre de décès
        évités se situerait entre 7.000 et 10.000. Ces chiffres doivent être
        interprétés avec prudence, mais ils montrent à quel point un retard de
        quelques jours peut avoir des conséquences dramatiques.
      </p>

      <p>
        C'est sur la durée que l'on pourra réellement comparer le succès des
        différentes stratégies opposées au SARS-CoV-2. Il faut en effet
        s'attendre à ce que l'épidémie resurgisse avec la sortie progressive du
        confinement, et les pays qui ont pris des mesures drastiques très tôt
        seront tout aussi exposés qu'à la première vague. Mais le retard de la
        France était irresponsable dès lors qu'aucune mesure sérieuse n'était
        prise ni pour maîtriser la propagation de l'épidémie dans des
        proportions acceptables, ni pour protéger les personnes vulnérables. En
        l'absence de traitement et de vaccin, seules deux voies sérieuses
        s'offrent aux gouvernements : l'atténuation, avec pour objectif
        d'atteindre l'immunité collective sans déborder le système de santé et
        en isolant les personnes vulnérables, et l'endiguement, qui consiste à
        faire régresser l'épidémie au maximum par des mesures de traçage et de
        distanciation sociale.
      </p>

      <h2>Tests : la France toujours à la traîne</h2>

      <p>
        Selon l'avis{" "}
        <a href="https://www.liberation.fr/sciences/2020/04/19/aux-alentours-du-11-mai-l-epidemie-sera-toujours-la_1785551">
          des chercheurs du{" "}
          <abbr title="Laboratoire Maladies Infectieuses et Vecteurs (Écologie, Génétique, Évolution et Contrôle)">
            Mivegec
          </abbr>{" "}
          interrogés par <em>Libération</em>
        </a>
        , ces stratégies impliquent toutes deux une capacité élevée de tests
        PCR, destinés à identifier les personnes contagieuses. Et là encore, la
        France n’est pas en avance :
      </p>

      <TestPolicy />

      <TestPerCapita />

      <p>
        On observe que la France fait partie des pays avec la politique de test
        la plus restrictive, ne ciblant que les cas sérieux avec une forte
        suspicion, pour un rythme relativement faible comparé aux autres pays
        (27<sup>ème</sup> sur 42 en terme de tests effectués par millier
        d’habitants). Cela n’est pas près de changer : lors de son allocation du
        lundi 14 avril, Emmanuel Macron a en effet annoncé qu’au 11 mai, soit la
        date du déconfinement, la France ne testerait que les personnes
        symptomatiques. Après avoir confiné trop tard, la France pourrait
        déconfiner trop tôt.
      </p>

      <p>
        <strong>Mise à jour du 28/04/2020</strong>
        <br />
        Dans son allocution devant l'Assemblée Nationale ce mardi 28 avril, le
        premier ministre Édouard Philippe a annoncé qu'à la sortie du
        confinement, la France serait dotée d'une capacité de 700.000 tests par
        semaine. Une capacité calibrée selon ses dires pour tester jusqu'à 25
        contacts symptomatiques ou non pour chaque personne testée comme
        contaminée, dans une limite de 4000 contaminations par jour. Mais si,
        comme l'estime l'équipe Covid-19 de l'Imperial College, 4 % des Français
        ont été contaminés, l'essentiel des contaminations ayant probablement eu
        lieu entre mars et avril, environ 44.000 contaminations par jour
        auraient eu lieu durant cette période, bien au-dessus de la capacité
        visée par le gouvernement.
      </p>

      <p>
        <strong>Mise à jour du 01/05/2020</strong>
        <br />
        Les simulations de nombre de décès ont été mises à jour à partir de
        données plus récentes.
      </p>
    </>
  );
}

export default CovidWorldResponse;
